import {
  ForwardedRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { yupResolver } from '@hookform/resolvers/yup';
import { useIntroducerData } from 'contexts/IntroducerData/IntroducerData.context';
import { useAmplitude } from 'hooks/useAmplitude';
import {
  useGetIntroducerApplication,
  useGetSignupState,
} from 'lendingApi/lendingApiHooks';
import {
  FormState,
  Resolver,
  useForm,
  useFormState,
  useWatch,
} from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { IwQueryClient } from 'store/IwQueryClient';
import { TImperativeHandleSignup } from 'types';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { useRouterApplicationId } from '../hooks/useRouterApplicationId';
import { useSignupData } from '../hooks/useSignupData';
import { useSignupStep } from '../hooks/useSignupStep';
import { EMPTY_DEBT } from '../RefinancingDebts/refinancing.types';
import { useShowRefinancing } from '../RefinancingDebts/useShowRefinancing.hook';
import { schema } from './SignUpForm.schema';
import { TSignupFormData } from './SignupForm.types';
import {
  clearHiddenRefinancing,
  getFrontendValidationErrors,
  parseBackendErrorMessage,
} from './SignUpFormHelper';

type TUseSignupFormParams = {
  ref: ForwardedRef<TImperativeHandleSignup>;
  setSaveForLaterLoading?: (data: boolean) => void;
};

export const useSignUpForm = ({
  ref,
  setSaveForLaterLoading,
}: TUseSignupFormParams) => {
  const navigate = useNavigate();
  const { trackEvent } = useAmplitude();
  const { updateLatestAllowedStep } = useSignupStep();
  const {
    stateKey,
    applicationData,
    stateData,
    updateSignupData,
    isReintroduction,
  } = useSignupData();
  const { checkIntroducerPermission } = useIntroducerData();
  const isDisabled = !checkIntroducerPermission('can_create_applications');
  const applicationId = useRouterApplicationId();
  const { state, stateStatus } = useGetSignupState();
  const { application, applicationStatus } = useGetIntroducerApplication();
  const stateHasLoaded = state && stateStatus === 'success';
  const applicationHasLoaded = application && applicationStatus === 'success';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);
  // True when `signupError` is our validation banner rather than a backend
  // error, so we only auto-dismiss it once validation passes.
  const isValidationBanner = useRef(false);
  const defaultValues = {
    // Company values
    companyName: stateData.companyName,
    companyNumber: stateData.companyNumber,
    companyType: stateData.companyType,
    tradingName: stateData.tradingName,
    website: stateData.website,
    startDateMonth: stateData.startDateMonth,
    startDateYear: stateData.startDateYear,
    last12MonthsTurnover: stateData.last12MonthsTurnover,
    isVatRegistered: stateData.isVatRegistered,
    // Client values
    applicant: stateData.applicant,
    guarantors: stateData.guarantors,
    // Loan application values
    amount: isReintroduction ? undefined : applicationData.amount,
    loanDuration: applicationData.loanDuration,
    loanPurpose: applicationData.loanPurpose,
    loanPurposeDetails: applicationData.loanPurposeDetails,
    // Fall back to a single empty debt so the section shows one blank card.
    refinancing: applicationData.refinancing ?? {
      debts: [{ ...EMPTY_DEBT }],
    },
  };

  const showRefinancingRef = useRef(false);
  const resolver = useMemo<Resolver<TSignupFormData>>(
    () => (values, context, options) =>
      yupResolver(schema(isReintroduction, showRefinancingRef.current))(
        values,
        context,
        options,
      ),
    [isReintroduction],
  );

  const methods = useForm<TSignupFormData>({
    resolver,
    defaultValues: defaultValues,
  });

  const [amount, loanPurpose] = useWatch({
    control: methods.control,
    name: ['amount', 'loanPurpose'],
  });
  const showRefinancing = useShowRefinancing({ amount, loanPurpose });
  showRefinancingRef.current = showRefinancing;

  // Dismiss the validation banner once every field error is resolved. The
  // `isValidationBanner` guard leaves backend errors untouched.
  const { errors } = useFormState({ control: methods.control });
  useEffect(() => {
    if (isValidationBanner.current && Object.keys(errors).length === 0) {
      isValidationBanner.current = false;
      setSignupError(null);
    }
  }, [errors]);

  // Clear refinancing errors and the submit banner when the section hides, so
  // stale errors don't flash back up if it's re-shown.
  useEffect(() => {
    if (!showRefinancing) {
      methods.clearErrors('refinancing');
      setSignupError(null);
    }
  }, [showRefinancing, methods]);

  useEffect(() => {
    if (applicationHasLoaded && stateHasLoaded) {
      methods.reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationHasLoaded, stateHasLoaded]);

  // React hook that allows this referenced component to return a single function instead of the entire return value of the element
  useImperativeHandle(
    ref,
    () => {
      return {
        async saveFormData() {
          setSaveForLaterLoading?.(true);
          await updateSignupData({
            data: clearHiddenRefinancing(
              methods.getValues(),
              showRefinancingRef.current,
            ),
            isSaveAndFinishLater: true,
          });
          navigate(PATH_NAMES.home());
          setSaveForLaterLoading?.(false);
        },
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [methods, updateSignupData],
  );

  const onSubmit = async (values: TSignupFormData) => {
    setIsSubmitting(true);
    setSignupError(null);

    try {
      if (!stateKey) {
        return;
      }

      IwQueryClient.getOrCreate().invalidateQueries({
        queryKey: ['offers'],
      });

      trackEvent(AMPLITUDE_EVENTS.newApplicationSubmitted, {
        'State key': stateKey,
        Reintroduction: isReintroduction,
      });

      const { updatedState } = await updateSignupData({
        data: clearHiddenRefinancing(values, showRefinancingRef.current),
      });

      await updateLatestAllowedStep('new/productOffers', updatedState);
      navigate(PATH_NAMES.newApplicationProductOffers({ applicationId }));
    } catch (e: unknown) {
      const errorMessage = parseBackendErrorMessage(e);
      isValidationBanner.current = false;
      setSignupError(errorMessage);
      trackError(errorMessage, true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const trackError = (error?: string, isBackendError = false) => {
    if (!error) return;

    trackEvent(AMPLITUDE_EVENTS.newApplicationSubmissionErrored, {
      'Error message': error,
      Reintroduction: isReintroduction,
      'Backend error': isBackendError,
    });

    datadogLogs.logger.error('Failed to submit application', {
      createError: error,
      stateKey,
      isReintroduction,
      isBackendError,
    });
  };

  // Handle Frontend Validation Error
  const onError = (errors: FormState<TSignupFormData>['errors']) => {
    if (!errors) return;

    isValidationBanner.current = true;
    setSignupError(
      'Some fields are missing or invalid. Please check and try again.',
    );
    getFrontendValidationErrors(errors).forEach((error) => trackError(error));
  };

  return {
    isDisabled,
    signupError,
    onSubmit,
    onError,
    isSubmitting,
    methods,
    stateHasLoaded,
    applicationHasLoaded,
    showRefinancing,
  };
};
